import { createRoot } from 'react-dom/client';
import './design/index.css';
import './app-owner/owner.css';
import { services } from './adapters';
// Test hook: only exists when the e2e harness (window.__qa) is present, never in a plain production page load.
if ((window as unknown as { __qa?: unknown }).__qa) (window as unknown as { __plServices?: unknown }).__plServices = services;
import { registerNamespace, setLocale, subscribeLocale, getLocale, LOCALES } from './i18n';
import type { Locale } from './domain/types';
import { ToastProvider } from './ui';
import { Routes } from './routes';
import { bindUrlMode, currentRoute, go, isPublicRoute, onRouteChange } from './config/urlmode';
import { AppShell } from './AppShell';
import { registerBookingLocale } from './bookingBridge';
import heCommon from './i18n/locales/he/common.json';
import heMessages from './i18n/locales/he/messages.json';
import heBooking from './i18n/locales/he/booking.json';
import heOwnerShell from './i18n/generated/ownerShell.json';

async function start(): Promise<void> {
  registerNamespace('he', 'common', heCommon);
  registerNamespace('he', 'messages', heMessages);
  registerNamespace('he', 'booking', heBooking);
  try { registerBookingLocale(); } catch { /* booking ships its own namespaces */ }
  // Entry bundle holds only the owner strings the demo banner needs; the full owner namespace is a separate chunk,
  // fetched at once for owner routes and when navigating there from a public page.
  registerNamespace('he', 'owner', heOwnerShell);
  bindUrlMode();   // clean-URL mode: converts legacy #/ links, keeps hash links working (no-op for the default hash build)
  const isPublic = (): boolean => isPublicRoute(currentRoute());
  let ownerLoaded = false;
  const loadOwner = (): Promise<void> => {
    if (ownerLoaded) return Promise.resolve();
    ownerLoaded = true;
    return import('./i18n/locales/he/owner.json').then((m) => registerNamespace('he', 'owner', m.default)).catch(() => { ownerLoaded = false; });
  };
  const ownerReady = isPublic() ? Promise.resolve() : loadOwner();
  onRouteChange(() => { if (!isPublic()) void loadOwner(); });
  let saved: string | null = null;
  try { saved = services.kv.get('locale') ?? null; } catch { /* storage blocked */ }
  // English and Arabic load on demand: awaited when the saved locale needs them, otherwise fetched in the background.
  const loadExtra = (): Promise<void> => import('./i18n/extraLocales').then((m) => m.registerExtraLocales());
  if (saved === 'en' || saved === 'ar') { try { await loadExtra(); } catch { /* falls back to Hebrew */ } }
  setLocale((LOCALES as readonly string[]).includes(saved ?? '') ? (saved as Locale) : 'he');
  try { await Promise.all([services.ready, ownerReady]); } catch { /* error states render per screen */ }
  createRoot(document.getElementById('root')!).render(
    <ToastProvider>
      <AppShell><Routes /></AppShell>
    </ToastProvider>,
  );
  // Native shell only (Capacitor injects window.Capacitor): push for new bookings / cancellations. Separate chunk, never loaded on the web.
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.()) {
    void import('./native/push').then((m) => m.startNativePush({ capacitor: cap, auth: services.auth, devices: services.devices, openRoute: (r) => go(r) })).catch(() => undefined);
  }
  // Static boot placeholder (index.html) stays until the page paints its own content; this is the safety net.
  setTimeout(() => document.getElementById('boot')?.remove(), 4000);
  // Hebrew never fetches the en/ar chunk; a later switch loads it on demand (Hebrew text shows until it arrives).
  let extraStarted = saved === 'en' || saved === 'ar';
  subscribeLocale(() => { const l = getLocale(); if (!extraStarted && (l === 'en' || l === 'ar')) { extraStarted = true; loadExtra().catch(() => { extraStarted = false; }); } });
  if ('serviceWorker' in navigator && location.protocol.startsWith('http') && !new URLSearchParams(location.search).has('nosw')) {
    const reg = () => { navigator.serviceWorker.register('/sw.js').catch(() => undefined); };
    if (document.readyState === 'complete') reg(); else window.addEventListener('load', reg);
  }
}
void start();
